/**
 * Award-data SSOT — Residential aged care fees, caps, subsidies
 *
 * Department of Health, Disability and Ageing — Schedule of Fees and
 * Charges for Residential Care from 20 March 2026.
 *
 * NOTE: This is regulator/program data, NOT a Fair Work modern award.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * TWO REGIMES — locked at admission
 * ═══════════════════════════════════════════════════════════════════════
 *
 *   - Pre-1-Nov-2025 admission → MTCF (Means Tested Care Fee) for life
 *   - From-1-Nov-2025 admission → NCCC (Non-Clinical Care Contribution)
 *
 * Discharge >28 days then re-admit resets to NCCC. The platform must store
 * `residents.fee_regime` and `residents.fee_regime_determined_at` and pick
 * the calculation path based on this — never compute both regimes for one
 * resident.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * SOURCES + INDEXATION
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Source: doh-schedule-of-fees-residential-20-sep-2026.pdf (Schedule of fees
 *         and charges for residential care from 20 September 2026).
 * Verified: 2026-09-30.
 * Artifact: docs/operations/clause-verifications/2026-09-30-regulatory-indexation-20-sep-and-1-oct-2026.md
 *
 * Indexation: 20 March and 20 September each year; the means tested care fee
 * maximum and MPIR/BIR also move on 1 January, 1 April, 1 July and 1 October.
 * Next indexation: 1 January 2027 (MPIR/BIR, MTCF maximum), then 20 March 2027.
 *
 * These constants hold the CURRENT schedule only. Date-effective amounts live
 * in residential_fee_rate_periods, contribution_cap_index_periods,
 * mpir_rate_history and bir_rate_history.
 */

// ═══════════════════════════════════════════════════════════════════════
// Common fees + interest rates
// ═══════════════════════════════════════════════════════════════════════

/**
 * Residential basic daily fee — applies to ALL residents (every regime),
 * regardless of means assessment. Charged for permanent residential care AND
 * residential respite.
 *
 * Source: doh-schedule-of-fees-residential-20-sep-2026.pdf, page 1.
 * Effective 20 September 2026 ($66.80 from 20 March to 19 September 2026).
 */
export const RESIDENTIAL_BASIC_DAILY_FEE = 68.93;

/**
 * Maximum accommodation supplement amount paid by the Commonwealth to providers
 * for fully or partially supported residents.
 *
 * Source: doh-schedule-of-fees-residential-20-sep-2026.pdf, page 1.
 * Effective 20 September 2026.
 */
export const MAX_ACCOMMODATION_SUPPLEMENT_DAILY = 73.73;

/**
 * Maximum Permissible Interest Rate (MPIR) for accommodation costs.
 *
 * Source: doh-schedule-of-fees-residential-20-sep-2026.pdf p.3,
 *         "Interest rates for accommodation costs" table, and doh-bir-mpir-2026-09.pdf.
 * Effective 1 October 2026 - 31 December 2026 (re-indexed quarterly; 8.43%
 * from 1 July to 30 September 2026). The rate that applies to a resident is
 * the one current on their entry or room-price agreement date, so money paths
 * must resolve it by date, not from this constant.
 */
export const MPIR_PCT = 8.51;

/** Base Interest Rate (BIR) from 1 October 2026 (3.25% from 1 April to
 *  30 September 2026) — used for legacy accommodation bond agreements
 *  (pre-1-Jul-2014 residents) and refund interest calculations. */
export const BASE_INTEREST_RATE_PCT = 3.75;

/** Maximum interest on outstanding accommodation charge from 20 September 2026.
 *  Used for accommodation charge agreements for pre-1-Jul-2014 residents under
 *  s.287-155 of the Aged Care Rules 2025. */
export const MAX_INTEREST_OUTSTANDING_ACCOM_CHARGE_PCT = 3.5;

// ═══════════════════════════════════════════════════════════════════════
// MTCF (post-1-July-2014 to pre-1-Nov-2025 residents)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Means Tested Care Fee — applies to residents admitted between 1 July 2014
 * and 31 October 2025 (inclusive). Replaced by NCCC for admissions from
 * 1 November 2025 onwards. Caps are LIFETIME-grandfathered for the resident
 * unless they discharge for >28 days then re-admit.
 *
 * Source: doh-schedule-of-fees-residential-20-sep-2026.pdf, page 3,
 * "Rates for post 1 July 2014 fee and accommodation arrangements" section.
 *
 * Note: the "daily" figure is the maximum means-tested care fee chargeable per
 * day, set by the Commonwealth and indexed quarterly: $403.80 (1 Oct 2025),
 * $370.39 (1 April 2026), $372.03 (1 July 2026), $380.46 (1 October 2026).
 *
 * `MTCF_CAPS.daily` is that published maximum. It is not the per-resident cap.
 * The per-resident cap is the resident's cost of care (Rules r 285A-14).
 * Billing code uses the cents table in `residential-fees/rates.ts`
 * (`residentialFeeRatesForV3`). These dollar constants stay for existing callers.
 */
export const MTCF_CAPS = {
  daily: 380.46, // Published maximum per day, not the per-resident cap. See residential-fees/rates.ts.
  annual: 37_055.98, // Annual cap on accumulated means tested care fee. From 20 September 2026.
  lifetime: 88_934.57, // Lifetime cap on accumulated means tested care fee. From 20 September 2026.
} as const;

// ═══════════════════════════════════════════════════════════════════════
// NCCC (1-Nov-2025-onwards residents)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Non-Clinical Care Contribution — applies to residents admitted from
 * 1 November 2025 onwards.
 *
 * IMPORTANT: The NCCC LIFETIME cap is SHARED with the SAH lifetime cap. Per
 * Services Australia: "Any contributions made by a care recipient towards
 * their Support at Home services before they enter residential aged care will
 * count towards the lifetime cap." Cross-module data sharing required.
 * The shared lifetime cap value matches `SAH_CONTRIBUTION_CAPS.lifetimeCapNewEntrant`.
 *
 * Source: doh-schedule-of-fees-residential-20-sep-2026.pdf, page 1.
 * Effective 20 September 2026.
 */
export const NCCC_CAPS = {
  daily: 109.45, // Daily maximum non-clinical care contribution.
  lifetime: 140_652.8, // Lifetime cap — matches SAH new-entrant cap by Commonwealth design.
  /**
   * 4-year time-limited cap. The fee ceases after a person pays it for four
   * years (1,460 days), even if they have not reached the lifetime cap amount.
   * Per the schedule footnote on page 1.
   */
  timeLimitDays: 1_460,
} as const;

/**
 * Hotelling Supplement Contribution (HSC) — separate maximum from NCCC, applies
 * to the everyday-living portion of residential care. Means-tested.
 *
 * Source: doh-schedule-of-fees-residential-20-sep-2026.pdf, page 1 (unchanged
 * at $22.15 since 20 September 2025).
 */
export const HSC_CAPS = {
  daily: 22.15, // Maximum daily hotelling contribution.
} as const;

/**
 * Inclusive residential fee periods mirrored from residential_fee_rate_periods.
 * History starts on 1 November 2025; the constants above hold the current schedule.
 */
export const RESIDENTIAL_FEE_RATE_PERIODS = [
  {
    effectiveFrom: '2025-11-01',
    effectiveTo: '2026-03-19',
    basicDailyFee: 65.55,
    maximumAccommodationSupplementDaily: 70.94,
    hotellingDaily: 22.15,
    ncccDaily: 105.3,
    standardLifetimeCap: 135_318.69,
    mtcfLifetimeCap: 84_571.66,
    mtcfAnnualCap: 35_238.11,
    mtcfDailyMax: 403.8,
  },
  {
    effectiveFrom: '2026-03-20',
    effectiveTo: '2026-03-31',
    basicDailyFee: 66.8,
    maximumAccommodationSupplementDaily: 72.3,
    hotellingDaily: 22.15,
    ncccDaily: 107.32,
    standardLifetimeCap: 137_917.01,
    mtcfLifetimeCap: 86_185.23,
    mtcfAnnualCap: 35_910.43,
    mtcfDailyMax: 403.8,
  },
  {
    effectiveFrom: '2026-04-01',
    effectiveTo: '2026-06-30',
    basicDailyFee: 66.8,
    maximumAccommodationSupplementDaily: 72.3,
    hotellingDaily: 22.15,
    ncccDaily: 107.32,
    standardLifetimeCap: 137_917.01,
    mtcfLifetimeCap: 86_185.23,
    mtcfAnnualCap: 35_910.43,
    mtcfDailyMax: 370.39,
  },
  {
    effectiveFrom: '2026-07-01',
    effectiveTo: '2026-09-19',
    basicDailyFee: 66.8,
    maximumAccommodationSupplementDaily: 72.3,
    hotellingDaily: 22.15,
    ncccDaily: 107.32,
    standardLifetimeCap: 137_917.01,
    mtcfLifetimeCap: 86_185.23,
    mtcfAnnualCap: 35_910.43,
    mtcfDailyMax: 372.03,
  },
  {
    effectiveFrom: '2026-09-20',
    effectiveTo: '2026-09-30',
    basicDailyFee: 68.93,
    maximumAccommodationSupplementDaily: 73.73,
    hotellingDaily: 22.15,
    ncccDaily: 109.45,
    standardLifetimeCap: 140_652.8,
    mtcfLifetimeCap: 88_934.57,
    mtcfAnnualCap: 37_055.98,
    mtcfDailyMax: 372.03,
  },
  {
    effectiveFrom: '2026-10-01',
    effectiveTo: null,
    basicDailyFee: 68.93,
    maximumAccommodationSupplementDaily: 73.73,
    hotellingDaily: 22.15,
    ncccDaily: 109.45,
    standardLifetimeCap: 140_652.8,
    mtcfLifetimeCap: 88_934.57,
    mtcfAnnualCap: 37_055.98,
    mtcfDailyMax: 380.46,
  },
] as const;

/**
 * Resolve the fee schedule for a valid YYYY-MM-DD charge date (inclusive bounds).
 * Dates before 1 November 2025 use the first period because no earlier history
 * is available in this table. Invalid date strings throw a RangeError.
 */
export function residentialFeeRatesFor(
  date: string,
): (typeof RESIDENTIAL_FEE_RATE_PERIODS)[number] {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
    !Number.isFinite(parsed.getTime()) ||
    parsed.getUTCFullYear() !== Number(date.slice(0, 4)) ||
    parsed.getUTCMonth() + 1 !== Number(date.slice(5, 7)) ||
    parsed.getUTCDate() !== Number(date.slice(8, 10))
  ) {
    throw new RangeError(`Invalid residential fee date: ${date}`);
  }

  return (
    RESIDENTIAL_FEE_RATE_PERIODS.find(
      (period) =>
        date >= period.effectiveFrom && (period.effectiveTo === null || date <= period.effectiveTo),
    ) ?? RESIDENTIAL_FEE_RATE_PERIODS[0]
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Means-test thresholds (NCCC regime — admitted post-1-Nov-2025)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Income thresholds for residential care means assessment.
 *
 * Source: doh-schedule-of-fees-residential-20-sep-2026.pdf, page 1.
 * Effective 20 September 2026.
 */
export const RESIDENTIAL_INCOME_THRESHOLDS = {
  single: {
    incomeFreeArea: 36_454.6,
    first: 90_130.04,
    second: 105_048.0,
    third: 121_173.2,
    fourth: 145_818.4,
  },
  coupleIllnessSeparated: {
    incomeFreeArea: 35_726.6,
    first: 89_402.04,
    second: 105_048.0,
    third: 121_173.2,
    fourth: 142_906.4,
  },
} as const;

/**
 * Asset thresholds for residential care means assessment.
 *
 * Source: doh-schedule-of-fees-residential-20-sep-2026.pdf, page 2.
 * Effective 20 September 2026.
 */
export const RESIDENTIAL_ASSET_THRESHOLDS = {
  assetFreeArea: 66_500.0,
  first: 219_858.4,
  second: 266_000.0,
  third: 369_366.66,
  fourth: 552_858.4,
  /** Net value of the home above this amount is excluded from the resident's
   *  assets. Applies separately to each member of a couple. */
  homeExemptionCap: 219_858.4,
} as const;

// ═══════════════════════════════════════════════════════════════════════
// Refundable accommodation deposits
// ═══════════════════════════════════════════════════════════════════════

/**
 * Source: doh-schedule-of-fees-residential-20-sep-2026.pdf, page 2,
 * "Thresholds for refundable deposits" table. Effective 20 September 2026.
 */
export const REFUNDABLE_DEPOSIT_THRESHOLDS = {
  /** Minimum assets a resident must be left with after paying their refundable
   *  deposit. */
  minimumPermissibleAssetLevel: 66_500,
  /** Maximum refundable accommodation deposit chargeable without prior IHACPA
   *  approval. */
  maximumWithoutApproval: 789_686,
} as const;

// ═══════════════════════════════════════════════════════════════════════
// Transition Care Program
// ═══════════════════════════════════════════════════════════════════════

/**
 * TCP maximum daily fees per the schedule's "Fees for Transition Care Program
 * (TCP)" table on page 6. Effective 20 September 2026.
 *
 * Source: doh-schedule-of-fees-residential-20-sep-2026.pdf, page 6.
 */
export const TCP_DAILY_FEES = {
  homeOrCommunity: 14.19,
  residentialSetting: 68.93,
} as const;

// ═══════════════════════════════════════════════════════════════════════
// Fee regime helper
// ═══════════════════════════════════════════════════════════════════════

/**
 * Helper: which fee regime applies to a resident based on admission date.
 * MTCF for admissions BEFORE 1 November 2025; NCCC for admissions FROM 1 Nov 2025.
 * If a resident is discharged for >28 days and re-admitted, the regime is reset
 * to NCCC (handle that in the application layer when processing readmissions).
 */
export const NCCC_REGIME_START_DATE = '2025-11-01' as const;

export function feeRegimeForAdmission(admissionDateIso: string): 'MTCF' | 'NCCC' {
  return admissionDateIso < NCCC_REGIME_START_DATE ? 'MTCF' : 'NCCC';
}
